import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAuth } from '../../contexts/AuthContext';
import { shJson } from './api';

const emptyForm = { id: null, name: '', description: '', active: true, integraContratos: false };

export default function SafetyHuddleAdminSetoresPage() {
  const navigate = useNavigate();
  const { unidadeSelecionada, isAdmin, rbacRole } = useAuth();
  const canManageRole =
    isAdmin ||
    ['SUPER_ADMIN', 'LOCAL_ADMIN', 'LOCAL_APPROVER'].includes(rbacRole);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sectors, setSectors] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [seeAll, setSeeAll] = useState(false);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!unidadeSelecionada?.codigo) {
      setSectors([]);
      setLoading(false);
      setError('Selecione uma unidade.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await shJson(
        '/api/safety-huddle/sectors?includeInactive=1',
        unidadeSelecionada
      );
      setSectors(data.items || []);
      setSeeAll(!!data.seeAll);
      setCanManage(!!data.canManage);
    } catch (e) {
      setError(e.message || 'Erro ao carregar setores.');
    } finally {
      setLoading(false);
    }
  }, [unidadeSelecionada]);

  useEffect(() => {
    load();
  }, [load]);

  const allowed = canManage && seeAll && canManageRole;

  const openCreate = () => {
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (s) => {
    setForm({
      id: s.id,
      name: s.name || '',
      description: s.description || '',
      active: !!s.active,
      integraContratos: !!s.integraContratos,
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      if (form.id) {
        await shJson(`/api/safety-huddle/sectors/${form.id}`, unidadeSelecionada, {
          method: 'PUT',
          body: JSON.stringify({
            name: form.name.trim(),
            description: form.description.trim(),
            active: form.active,
            integraContratos: !!form.integraContratos,
          }),
        });
      } else {
        await shJson('/api/safety-huddle/sectors', unidadeSelecionada, {
          method: 'POST',
          body: JSON.stringify({
            name: form.name.trim(),
            description: form.description.trim(),
            integraContratos: !!form.integraContratos,
          }),
        });
      }
      setOpen(false);
      setForm(emptyForm);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (s) => {
    if (!window.confirm(`Desativar o setor «${s.name}»? Formulários e histórico são preservados.`)) {
      return;
    }
    setError('');
    try {
      await shJson(`/api/safety-huddle/sectors/${s.id}`, unidadeSelecionada, {
        method: 'DELETE',
      });
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleReactivate = async (s) => {
    setError('');
    try {
      await shJson(`/api/safety-huddle/sectors/${s.id}/reativar`, unidadeSelecionada, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  if (!loading && !allowed) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/safety-huddle')} sx={{ mb: 2 }}>
          Voltar
        </Button>
        <Alert severity="warning">
          Acesso restrito a Admin, Diretoria ou Setor de Contratos desta unidade.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/safety-huddle')} sx={{ mb: 2 }}>
        Voltar ao hub
      </Button>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} color="primary.main">
            Admin · Setores
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cadastro da unidade
            {unidadeSelecionada?.nome || unidadeSelecionada?.codigo
              ? ` ${unidadeSelecionada.nome || unidadeSelecionada.codigo}`
              : ''}
            {' — sem exclusão destrutiva'}
          </Typography>
        </Box>
        {allowed && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Novo setor
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper elevation={0} sx={{ overflowX: 'auto', border: '1px solid rgba(25,118,210,0.12)' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Integra contratos</TableCell>
                <TableCell align="right">Formulários</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sectors.map((s) => (
                <TableRow key={s.id} hover sx={{ opacity: s.active ? 1 : 0.7 }}>
                  <TableCell>
                    <Typography fontWeight={600}>{s.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {s.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {s.integraContratos ? (
                      <Chip size="small" color="primary" variant="outlined" label="Sim" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Não
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{s.formsCount ?? 0}</TableCell>
                  <TableCell>
                    {s.deleted ? (
                      <Chip size="small" color="default" label="Desativado" />
                    ) : s.active ? (
                      <Chip size="small" color="success" label="Ativo" />
                    ) : (
                      <Chip size="small" color="warning" label="Inativo" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Abrir setor">
                      <span>
                        <IconButton
                          size="small"
                          disabled={!s.active}
                          onClick={() => navigate(`/safety-huddle/setores/${s.id}`)}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <span>
                        <IconButton
                          size="small"
                          disabled={!!s.deleted}
                          onClick={() => openEdit(s)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {s.active ? (
                      <Tooltip title="Desativar">
                        <IconButton size="small" color="warning" onClick={() => handleDeactivate(s)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Reativar">
                        <IconButton size="small" color="primary" onClick={() => handleReactivate(s)}>
                          <RestartAltIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!sectors.length && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Alert severity="info" sx={{ my: 1 }}>
                      Nenhum setor nesta unidade.
                    </Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{form.id ? 'Editar setor' : 'Novo setor'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome"
            fullWidth
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Descrição"
            fullWidth
            multiline
            minRows={2}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          <FormControlLabel
            sx={{ mt: 1, display: 'block' }}
            control={
              <Switch
                checked={!!form.integraContratos}
                onChange={(e) => setForm((f) => ({ ...f, integraContratos: e.target.checked }))}
              />
            }
            label="Integrar contratos da unidade (lista de prestadores nas perguntas Seleção)"
          />
          {form.id && (
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={
                <Switch
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                />
              }
              label="Setor ativo"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={saving || !form.name.trim()}
            onClick={handleSave}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
